import Stores from '../config/stores';
import { formatIngredientLabel, normalize } from './recipes';

const FALLBACK_STORE_NAME_FIXES = [
  [/Super\s*Brugsen/gi, 'Super Brugsen'],
  [/Dagli'\s*Brugsen/gi, 'Dagli Brugsen'],
  [/REMA\s*1000/gi, 'Rema 1000'],
  [/Coop\s*365/gi, 'Coop 365'],
];

const INGREDIENT_ROLE_PRIORITY = {
  kerne: 4,
  baerende: 3,
  stoette: 2,
  smagsgiver: 1.5,
  basis: 1,
  krydreskab: 0.25,
  variation: 0,
};

export function normalizeStoreName(value) {
  return normalize(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

export function getReadableStoreName(storeKey) {
  const configuredName = normalize(Stores[storeKey]?.name);
  const fallbackName = normalize(storeKey)
    .replace(/([a-zA-Z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-zÆØÅæøå])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-zÆØÅæøå])/g, '$1 $2');

  let name = configuredName || fallbackName;
  for (const [pattern, replacement] of FALLBACK_STORE_NAME_FIXES) {
    name = name.replace(pattern, replacement);
  }
  return name.replace(/\s+/g, ' ').trim();
}

export function getSelectedStoreNames(storeKeys) {
  return (Array.isArray(storeKeys) ? storeKeys : []).map(getReadableStoreName);
}

export function getUniqueMatchedOffers(recipe) {
  const seen = new Set();
  return (Array.isArray(recipe?.matched_offers) ? recipe.matched_offers : []).filter((offer) => {
    const key = [offer.produkt_navn, offer.butik, offer.match_source].join('|');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function getIngredientOfferMeta(recipe, productName) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  return (
    ingredients.find(
      (ingredient) => normalize(ingredient.produkt_navn).toLowerCase() === normalize(productName).toLowerCase()
    ) || null
  );
}

function scoreMatchedOffer(recipe, offer, index) {
  const ingredient = getIngredientOfferMeta(recipe, offer?.produkt_navn);
  const offerWeight = Number(ingredient?.tilbudsvaegt || 0);
  const roleKey = normalize(ingredient?.anbefalingsrolle).toLowerCase();
  const rolePriority = INGREDIENT_ROLE_PRIORITY[roleKey] ?? 0;
  const matchStrength = Number(offer?.match_strength || 0);
  const discount = Number(offer?.rabat_pct || 0);
  const isCoreDriver = normalize(ingredient?.er_kernedriver).toLowerCase() === 'ja';

  return (
    offerWeight * 100 +
    rolePriority * 10 +
    (isCoreDriver ? 15 : 0) +
    matchStrength * 8 +
    Math.min(Math.max(discount, 0), 50) * 0.2 -
    index * 0.01
  );
}

export function getSortedMatchedOffers(recipe, recommendation) {
  return getUniqueMatchedOffers(recommendation)
    .filter((offer) => offer.match_source === 'name')
    .map((offer, index) => ({
      offer,
      score: scoreMatchedOffer(recipe, offer, index),
    }))
    .sort((left, right) => {
      return (
        right.score - left.score ||
        Number(right.offer?.match_strength || 0) - Number(left.offer?.match_strength || 0) ||
        Number(right.offer?.rabat_pct || 0) - Number(left.offer?.rabat_pct || 0) ||
        formatIngredientLabel(left.offer?.produkt_navn).localeCompare(
          formatIngredientLabel(right.offer?.produkt_navn),
          'da-DK'
        )
      );
    })
    .map((entry) => entry.offer);
}

export function getFeaturedOfferLabel(recipe, recommendation) {
  const [featuredOffer] = getSortedMatchedOffers(recipe, recommendation);
  return featuredOffer ? formatIngredientLabel(featuredOffer.produkt_navn) : '';
}

export function buildRecommendationReason(recipe) {
  const offers = getUniqueMatchedOffers(recipe).filter((offer) => offer.match_source === 'name');
  const offerNames = offers
    .map((offer) => formatIngredientLabel(offer.produkt_navn))
    .filter(Boolean)
    .slice(0, 2);
  const nutritionVisible = recipe?.nutrition?.vises_offentligt !== false;
  const protein = nutritionVisible ? Number(recipe?.nutrition?.protein_pr_portion_g || 0) : 0;

  if (offerNames.length >= 2) {
    return `Anbefalet fordi ${offerNames[0].toLowerCase()} og ${offerNames[1].toLowerCase()} er på tilbud`;
  }
  if (offerNames.length === 1 && protein >= 25) {
    return `God værdi denne uge med ${offerNames[0].toLowerCase()} på tilbud og god mæthed`;
  }
  if (offerNames.length === 1) {
    return `Passer godt til ugens tilbud på ${offerNames[0].toLowerCase()}`;
  }
  if (protein >= 25) {
    return 'Høj protein og relevant for en nem hverdagsmiddag';
  }
  return 'God hverdagsret med flere tilbudsvarer i denne uge';
}

export function buildNutritionHighlight(recipe) {
  const nutrition = recipe?.nutrition || {};
  if (nutrition.vises_offentligt === false) {
    return '';
  }
  const protein = Number(nutrition.protein_pr_portion_g || 0);
  const kcal = Number(nutrition.kcal_pr_portion || 0);

  if (protein > 0) {
    return `${Math.round(protein)} g protein pr. portion`;
  }
  if (kcal > 0) {
    return `${Math.round(kcal)} kcal pr. portion`;
  }
  return '';
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatDkk(value) {
  const amount = toNumber(value);
  if (amount === null) {
    return '';
  }
  return `${Math.round(amount)} kr.`;
}

export function buildWeeklyShoppingPrice(recipe) {
  const amount = toNumber(recipe?.public_price?.pris_denne_uge_dkk);
  if (amount === null) {
    return '';
  }
  return `Køb ind for ${formatDkk(amount)}`;
}

export function buildWeeklySavingsLabel(recipe) {
  const amount = toNumber(recipe?.public_price?.besparelse_dkk);
  if (amount === null || amount <= 0) {
    return '';
  }
  return `Spar ca. ${formatDkk(amount)} i denne uge`;
}

export function selectHomepageRecommendations(weeklyData, selectedStoreNames, limit = 4) {
  const topRecommendations = Array.isArray(weeklyData?.top_recommendations)
    ? weeklyData.top_recommendations
    : [];
  const rankedCandidates = Array.isArray(weeklyData?.ranked_candidates)
    ? weeklyData.ranked_candidates
    : [];
  const normalizedSelectedStores = new Set(
    (Array.isArray(selectedStoreNames) ? selectedStoreNames : []).map(normalizeStoreName)
  );
  const recommendations = normalizedSelectedStores.size > 0 && rankedCandidates.length > 0
    ? rankedCandidates
    : topRecommendations;

  if (normalizedSelectedStores.size === 0) {
    return recommendations.slice(0, limit);
  }

  const scored = recommendations
    .map((recipe) => {
      const matchedOffers = getUniqueMatchedOffers(recipe);
      const selectedStoreMatches = matchedOffers.filter((offer) =>
        normalizedSelectedStores.has(normalizeStoreName(offer.butik))
      );
      return {
        recipe,
        selectedStoreMatches,
      };
    })
    .sort((left, right) => {
      const scoreA = Number(left.recipe.samlet_score_foreloebig_0_100 || 0);
      const scoreB = Number(right.recipe.samlet_score_foreloebig_0_100 || 0);
      return (
        right.selectedStoreMatches.length - left.selectedStoreMatches.length ||
        scoreB - scoreA
      );
    });

  const preferred = scored
    .filter((entry) => entry.selectedStoreMatches.length > 0)
    .map((entry) => entry.recipe);

  if (preferred.length >= limit) {
    return preferred.slice(0, limit);
  }

  return preferred.slice(0, limit);
}
